import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import styled from '@emotion/styled';
import { partition } from 'lodash';
import React, { FC } from 'react';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box } from '~Styles';
import useI18n from '~Translations';
import DueLaterDivider from '../DueLaterDivider';
import { EnrichedOrder } from '~Scenes/EventedKitchenDisplay/view';

const LiteColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 100%;
`;

const LiteColumn = styled.div`
  padding: 16px;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  overflow: hidden;

  &:first-child {
    border-right: 1px solid #2b2f38;
  }
`;

const LiteOrdersList = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: scroll;
`;

const UpcomingWrapper = styled.div`
  margin-top: 24px;
  opacity: 0.6;
`;

const UpcomingTitle = styled.div`
  color: #fff;
  font-size: 18px;
  margin-bottom: 12px;
`;

const OrderCount = styled.span`
  font-size: 14px;
  aspect-ratio: 1;
  height: 24px;
  width: 24px;
  background-color: var(--colorWhite);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: var(--colorBlack);
  padding-bottom: 1px;
`;

interface Props {
  regularKDSOrders: EnrichedOrder[];
  ordersDueLaterToday: EnrichedOrder[];
  upcomingOrders: EnrichedOrder[];
  renderOrders: (orders: EnrichedOrder[]) => React.ReactNode;
}

const KdsLite: FC<Props> = ({ regularKDSOrders, ordersDueLaterToday, upcomingOrders, renderOrders }) => {
  const { i18n } = useI18n();
  const [ongoingOrders, readyOrders] = partition(
    regularKDSOrders,
    (order) => order.order.status !== ORDER_STATUS.READY,
  );

  const [onGoingLaterToday, readyLaterToday] = partition(
    ordersDueLaterToday,
    (order) => order.order.status !== ORDER_STATUS.READY,
  );

  return (
    <LiteColumns>
      <LiteColumn>
        <Heading color="#fff" level={3} bottom={16}>
          <Box display="flex" alignItems="center" gap="xs">
            <span>{i18n(`KDS.ACCEPTED`)}</span>
            <OrderCount>{ongoingOrders?.length || '0'}</OrderCount>
          </Box>
        </Heading>
        <LiteOrdersList>
          {renderOrders(ongoingOrders)}

          {onGoingLaterToday?.length > 0 && (
            <>
              <DueLaterDivider />
              {renderOrders(onGoingLaterToday)}
            </>
          )}

          {upcomingOrders.length > 0 && (
            <UpcomingWrapper>
              <UpcomingTitle>{i18n('KDS.Upcoming') || 'Upcoming'}</UpcomingTitle>
              {renderOrders(upcomingOrders)}
            </UpcomingWrapper>
          )}
        </LiteOrdersList>
      </LiteColumn>
      <LiteColumn>
        <Heading color="#fff" level={3} bottom={16}>
          <Box display="flex" alignItems="center" gap="xs">
            <span>{i18n(`KDS.${ORDER_STATUS.READY}`)}</span>
            <OrderCount>{readyOrders?.length || '0'}</OrderCount>
          </Box>
        </Heading>
        {readyOrders?.length > 0 && (
          <LiteOrdersList>
            {renderOrders(readyOrders)}
            {readyLaterToday.length > 0 && (
              <>
                <DueLaterDivider />
                {renderOrders(readyLaterToday)}
              </>
            )}
          </LiteOrdersList>
        )}
      </LiteColumn>
    </LiteColumns>
  );
};

export default KdsLite;
